/* When one technician's day starts and ends.
 *
 * The board has always known when the SALON opens and shuts — see the business
 * hours note in store.js — and has measured walk-ins against the closing time
 * since. What it could not know is that Maria goes at five on a Thursday. The
 * desk knew; the board went on offering her a ninety-minute set at half four,
 * and the argument happened at the chair.
 *
 * So every technician gets two times of their own for the day, set by
 * right-clicking their name. Neither is stored until somebody changes one: the
 * pair below are what the board assumes, and they are deliberately the pair
 * that changes nothing.
 *
 *   IN    when they were marked in — except that a sheet set up after the
 *         doors opened says when somebody switched the tablet on, not when the
 *         floor arrived. Everybody on it at that point counts from opening
 *         time. Anyone added later arrived when they were added.
 *
 *   OUT   closing time and a little after, because the last square of the day
 *         usually runs over and nobody clocks off mid-service.
 *
 * The out time is the one that does any work, and only when it is EARLIER than
 * closing. A shift ending at or past the close is not a constraint the board
 * has to enforce — `closingOverrun` already measures work against the doors,
 * and having two rules for the end of the day would mean two answers to the
 * same question. `earlyEndAt` is where that is decided, and everything
 * downstream takes its answer rather than the raw time.
 *
 * Pure and tested for the usual reason: what this is for is the boundary —
 * quarter past four with a five o'clock finish and a forty-five minute set —
 * and nobody exercises that by hand at a desk with a client standing at it.
 */

/* How wide "the sheet was being set up" is.
 *
 * `confirmAddStaff` marks the morning crew in within the same click, but a
 * manager tapping six names one at a time takes a couple of minutes over it,
 * and every one of those people was in from opening. Five minutes covers that
 * and is still nowhere near a genuine late arrival, who turns up an hour after
 * the doors open, not four minutes.
 */
export var SETUP_WINDOW_MIN = 5;

/* When they started, as the board assumes it.
 *
 *   presentSince    when they were marked in today
 *   hours           today's { openAt, closeAt }, or null — no hours set, or
 *                   a day the salon is shut
 *   sheetStartedAt  the earliest anybody was marked in today, or null
 *
 * With no hours there is nothing to correct against, so the answer is simply
 * when they were marked in. That is also what a salon that never fills the
 * hours in gets, which is what it had before any of this existed.
 */
export function defaultShiftStart(presentSince, hours, sheetStartedAt) {
  if (presentSince == null) return null;
  if (!hours) return presentSince;
  var setup = sheetStartedAt == null ? presentSince : sheetStartedAt;
  // The opening crew, however late the tablet was switched on.
  if (presentSince <= setup + SETUP_WINDOW_MIN * 60000) return hours.openAt;
  // Somebody added afterwards arrived when they were added — but nobody was
  // working before the doors opened, whatever the sheet says.
  return Math.max(presentSince, hours.openAt);
}

// When they finish, as the board assumes it: closing time and a little after.
// Null with no hours — nothing says when the day ends, so nothing is assumed.
export function defaultShiftEnd(hours, graceMs) {
  return hours ? hours.closeAt + (graceMs || 0) : null;
}

/* The end of their day, but only when it comes before the end of the salon's.
 *
 * This is the whole of "is this a constraint at all". A default shift, or one
 * somebody typed to run past closing, hands back null and every check below
 * goes quiet — which is why setting hours for the salon cannot on its own
 * change what the board offers.
 */
export function earlyEndAt(endAt, closeAt) {
  if (endAt == null) return null;
  if (closeAt != null && endAt >= closeAt) return null;
  return endAt;
}

// Whether work of this length, started then, would be over before they go.
// True whenever there is no early finish to measure against.
export function fitsBeforeEnd(endAt, at, needMs) {
  if (endAt == null) return true;
  return at + (needMs || 0) <= endAt;
}

/* Whether their day is over as far as the board is concerned: there is no
 * longer room before they leave for the SHORTEST thing they do.
 *
 * Not the same as having left. A technician who goes at five and whose quickest
 * service takes forty minutes is finished at twenty past four — anything handed
 * to them after that either runs past the time they go or has to be taken off
 * them again, and the point of this is that neither happens. From then on their
 * remaining squares are blocked off, because the rotation has nothing left to
 * put in them.
 *
 * `shortestMs` null means their service list says nothing about length. Then
 * the only honest answer is the clock: they are finished when they are gone.
 */
export function shiftSpent(endAt, at, shortestMs) {
  if (endAt == null) return false;
  if (at >= endAt) return true;
  if (shortestMs == null) return false;
  return at + shortestMs > endAt;
}

/* Whether the board should now mark this technician done for the day.
 *
 * `shiftSpent` above stops OFFERING them work; this is the other half, and the
 * two are deliberately different moments. Spent is "there is no room left for
 * anything they do", which lands before they go — twenty past four for a five
 * o'clock finish. This is "they have gone", and it is the fact the rest of the
 * board has been waiting for the desk to type in: the badge on their name, the
 * column out of the rotation, the day-end idle total that should stop counting.
 *
 * Two things have to be true, and both were in the request:
 *
 *   the out time has passed   `at >= endAt`, on the nose. Not `early`, and this
 *                             is the one place that difference matters. `early`
 *                             is null for a finish at or past closing because a
 *                             finish that late constrains nothing — but a
 *                             technician who said seven thirty has still gone at
 *                             seven thirty, and the board should say so.
 *   their last service ended  `busy` is the whole of it. Nobody clocks off
 *                             mid-service, and marking somebody done with a
 *                             client in their chair would put a Done badge over
 *                             a running square.
 *
 * Only ever off a time somebody TYPED. The assumed finish is closing plus a
 * grace and belongs to everybody, so acting on it would mark the whole floor
 * done half an hour after the doors shut, every night, for a salon that never
 * touched this feature. `customEnd` is what separates a decision from a default.
 *
 * `floor` is where they stand right now — `{ present, busy, skipped }`. Somebody
 * already out of the rotation is left exactly as they are: whatever the desk
 * ticked is a more specific answer than this one, and overwriting `break` with
 * `done` would be the board arguing with the person who can see the room.
 */
export function doneForDayDue(shift, at, floor) {
  if (!shift || !shift.customEnd || shift.endAt == null) return false;
  if (at < shift.endAt) return false;
  if (!floor || !floor.present) return false;
  if (floor.busy || floor.skipped) return false;
  return true;
}
